import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

// ── Action Menu ──────────────────────────────────────────────────────────────
const ActionMenu = ({ role, onView, onToggle }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleToggle = () => {
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const menuHeight = 100;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight
                ? rect.top - menuHeight + window.scrollY
                : rect.bottom + window.scrollY;
            setMenuPos({ top, left: rect.right - 180 });
        }
        setOpen(!open);
    };

    const items = [
        {
            icon: 'bi-eye',
            label: 'View Role',
            color: '#1565c0',
            onClick: () => { onView(role); setOpen(false); }
        },
        {
            icon: role.status ? 'bi-pause-circle' : 'bi-play-circle',
            label: role.status ? 'Deactivate' : 'Activate',
            color: role.status ? '#dc2626' : '#16a34a',
            onClick: () => { onToggle(role._id, role.status); setOpen(false); }
        },
    ];

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button style={menuStyles.trigger} onClick={handleToggle}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <div style={{ ...menuStyles.menu, position: 'fixed', top: menuPos.top, left: menuPos.left }}>
                    {items.map((item, i) => (
                        <button key={i} style={menuStyles.item} onClick={item.onClick}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                            <span style={{ ...menuStyles.icon, background: item.color + '15', color: item.color }}>
                                <i className={`bi ${item.icon}`}></i>
                            </span>
                            <span style={{ ...menuStyles.label, color: item.color }}>{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const menuStyles = {
    trigger: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', fontSize: '15px' },
    menu: { position: 'absolute', right: 0, background: 'white', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', minWidth: '170px', zIndex: 1000, padding: '6px' },
    item: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '8px 10px', border: 'none', background: 'white', borderRadius: '6px', cursor: 'pointer', textAlign: 'left' },
    icon: { width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 },
    label: { fontSize: '13px', fontWeight: '500' },
};

// ── Main Component ───────────────────────────────────────────────────────────
const UserRolesComponent = () => {
    const [roles, setRoles] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedRole, setSelectedRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchRoles = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/role`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch roles');
            const data = await response.json();
            setRoles(data || []);
            setFilteredRoles(data || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            setError('Failed to load roles. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchRoles(); }, [fetchRoles]);

    const handleSearchTermChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
        setFilteredRoles(roles.filter(role =>
            role.role_name.toLowerCase().includes(value.toLowerCase())
        ));
    };

    const toggleRoleStatus = async (roleId, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/rolestatusupdate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ roleId }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to update role status.', { autoClose: 3000 });
                return;
            }
            toast.success(`Role ${currentStatus ? 'deactivated' : 'activated'} successfully!`, { autoClose: 3000 });
            setRoles(prev => prev.map(r => r._id === roleId ? { ...r, status: !currentStatus } : r));
            setFilteredRoles(prev => prev.map(r => r._id === roleId ? { ...r, status: !currentStatus } : r));
        } catch (error) {
            console.error('Error updating role status:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedRoles = filteredRoles.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Roles</h5>
                        <p style={styles.pageSubtitle}>Manage access roles and permissions</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={() => navigate('/manageuserrole')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Create Role
                    </button>
                </div>

                {/* Search */}
                <div style={styles.searchRow}>
                    <div style={styles.searchWrapper}>
                        <i className="bi bi-search" style={styles.searchIcon}></i>
                        <input
                            type="text"
                            placeholder="Search by role name..."
                            value={searchTerm}
                            onChange={(e) => handleSearchTermChange(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <span style={styles.resultCount}>{filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Table card */}
                <div style={styles.card}>
                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading roles…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchRoles}>Retry</button>
                        </div>
                    ) : filteredRoles.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-shield-lock" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>{searchTerm ? 'No roles match your search.' : 'No roles found. Create your first role.'}</p>
                            {!searchTerm && <button style={styles.btnPrimary} onClick={() => navigate('/manageuserrole')}>Create Role</button>}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Role Name</th>
                                        <th style={styles.th}>Created By</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Created On</th>
                                        <th style={styles.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRoles.map((role) => (
                                        <tr key={role._id} style={styles.tr}>
                                            <td style={styles.td}><strong>{role.role_name}</strong></td>
                                            <td style={styles.td}>{role.owner_name}</td>
                                            <td style={styles.td}>
                                                <span style={{ ...styles.badge, ...(role.status ? styles.badgeActive : styles.badgeInactive) }}>
                                                    {role.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{role.createdAt}</td>
                                            <td style={styles.td}>
                                                <ActionMenu role={role} onView={setSelectedRole} onToggle={toggleRoleStatus} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!isLoading && !error && filteredRoles.length > 0 && (
                    <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}

                <ToastContainer />

                {/* View Role Modal */}
                {selectedRole && (
                    <div style={styles.modalOverlay} onClick={() => setSelectedRole(null)}>
                        <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div style={styles.modalHeader}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={styles.modalIcon}>
                                        <i className="bi bi-shield-lock-fill" style={{ fontSize: 16, color: '#1565c0' }}></i>
                                    </div>
                                    <div>
                                        <h6 style={styles.modalTitle}>{selectedRole.role_name}</h6>
                                        <p style={styles.modalSubtitle}>
                                            {selectedRole.role_json?.length || 0} module{(selectedRole.role_json?.length || 0) !== 1 ? 's' : ''} assigned
                                        </p>
                                    </div>
                                </div>
                                <button style={styles.closeBtn} onClick={() => setSelectedRole(null)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>

                            {/* Body */}
                            <div style={styles.modalBody}>
                                {selectedRole.role_json?.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {selectedRole.role_json.map((module) => (
                                            <div key={module.id} style={styles.moduleCard}>
                                                <div style={styles.moduleHeader}>
                                                    <div style={styles.moduleIconWrap}>
                                                        <i className={`bi ${module.icon || 'bi-grid'}`} style={{ fontSize: 13, color: '#1565c0' }}></i>
                                                    </div>
                                                    <span style={styles.moduleName}>{module.name}</span>
                                                    {module.subModules?.length > 0 && (
                                                        <span style={styles.subBadge}>{module.subModules.length} sub</span>
                                                    )}
                                                </div>
                                                {module.subModules?.length > 0 && (
                                                    <div style={styles.subModuleList}>
                                                        {module.subModules.map((sub) => (
                                                            <span key={sub.id} style={styles.subModuleChip}>
                                                                <i className="bi bi-check" style={{ marginRight: 4 }}></i>
                                                                {sub.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={styles.emptyState}>
                                        <i className="bi bi-shield-exclamation" style={{ fontSize: 32, color: '#d1d5db' }}></i>
                                        <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>No modules assigned to this role.</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div style={styles.modalFooter}>
                                <span style={{ marginRight: 'auto' }}>
                                    <span style={{ ...styles.badge, ...(selectedRole.status ? styles.badgeActive : styles.badgeInactive) }}>
                                        {selectedRole.status ? 'Active' : 'Inactive'}
                                    </span>
                                </span>
                                <button style={styles.btnSecondary} onClick={() => setSelectedRole(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    searchRow: { display: 'flex', alignItems: 'center', gap: '12px' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: '360px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' },
    searchInput: { width: '100%', padding: '9px 12px 9px 36px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
    resultCount: { fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', borderBottom: '2px solid #bfdbfe' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeActive: { background: '#dcfce7', color: '#16a34a' },
    badgeInactive: { background: '#fee2e2', color: '#dc2626' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modalBox: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '80vh', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    modalIcon: { width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    modalTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    modalSubtitle: { margin: '2px 0 0', fontSize: '12px', color: '#6b7280' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0 },
    modalBody: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
    modalFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    moduleCard: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px', borderLeft: '3px solid #1565c0' },
    moduleHeader: { display: 'flex', alignItems: 'center', gap: 8 },
    moduleIconWrap: { width: '26px', height: '26px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    moduleName: { fontSize: '14px', fontWeight: '600', color: '#111827', flex: 1 },
    subBadge: { fontSize: '11px', background: '#eff6ff', color: '#1565c0', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' },
    subModuleList: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' },
    subModuleChip: { display: 'inline-flex', alignItems: 'center', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '500' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' },
};

export default UserRolesComponent;
